import { http } from "@api/base";
import {
  validateProfileImage,
  FILE_SIZE_LIMITS,
  EXTENDED_IMAGE_TYPES,
} from "@utils/fileUtils";

const PROFILE_BASE_URL = process.env.REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL;

const API_PATHS = {
  imageInfo: "/profile/image-info",
  userImageInfo: "/profile/user-image-info",
  uploadImage: "/profile/upload-image",
  deleteImage: "/profile/delete-image",
  profileData: "/profile/profile-data",
  updateProfile: "/profile/update-profile",
};

class ProfileApiError extends Error {
  constructor(message, code = null, response = null) {
    super(message);
    this.name = "ProfileApiError";
    this.code = code;
    this.response = response;
  }
}

const handleError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const errorMessage = data?.error || data?.message || "An error occurred";

    const statusMessages = {
      400: errorMessage || "Invalid request data",
      401: "Authentication required. Please log in.",
      403: "Permission denied",
      404: "Profile service not found. Please contact support.",
      413: `File is too large. Maximum size is ${
        FILE_SIZE_LIMITS.MAX_5MB / (1024 * 1024)
      }MB`,
      500: errorMessage || "Server error. Please try again later.",
    };

    const statusCodes = {
      400: "BAD_REQUEST",
      401: "UNAUTHORIZED",
      403: "FORBIDDEN",
      404: "NOT_FOUND",
      413: "FILE_TOO_LARGE",
      500: "SERVER_ERROR",
    };

    throw new ProfileApiError(
      statusMessages[status] || `Request failed with status ${status}`,
      statusCodes[status] || "UNKNOWN_ERROR",
      error.response
    );
  }

  if (
    error.code === "NETWORK_ERROR" ||
    error.message?.includes("Network Error")
  ) {
    throw new ProfileApiError(
      "Network error. Please check your connection.",
      "NETWORK_ERROR"
    );
  }

  throw new ProfileApiError(
    error.message || "An unexpected error occurred",
    "UNKNOWN_ERROR"
  );
};

const fixImageUrl = (url) => {
  if (url && !url.startsWith("http")) {
    return `${PROFILE_BASE_URL}${url}`;
  }
  return url;
};

const validateImageFile = (file) => {
  // Use centralized validation
  const validation = validateProfileImage(file);

  return {
    valid: validation.valid,
    errors: validation.errors,
    fileInfo: validation.fileInfo,
  };
};

// Get current user's image info
const getImageInfo = async () => {
  try {
    const response = await http.get(API_PATHS.imageInfo);
    const data = response.data;
    data.image_url = fixImageUrl(data.image_url);
    return data;
  } catch (error) {
    handleError(error);
  }
};

// Get specific user's image info by user ID
const getUserImageInfo = async (userId) => {
  try {
    const response = await http.get(`${API_PATHS.userImageInfo}/${userId}`);
    const data = response.data;
    if (data.image_url) {
      data.image_url = fixImageUrl(data.image_url);
    }
    return data;
  } catch (error) {
    // If user image not found, return null instead of throwing error
    if (error.response && error.response.status === 404) {
      return { image_url: null, has_image: false, user_id: userId };
    }
    handleError(error);
  }
};

// Batch fetch multiple user images for better performance
const getBatchUserImages = async (userIds) => {
  try {
    const response = await http.post(`${API_PATHS.userImageInfo}/batch`, {
      user_ids: userIds,
    });
    const data = response.data;

    // Fix image URLs in batch response
    if (data.images) {
      Object.keys(data.images).forEach((userId) => {
        if (data.images[userId].image_url) {
          data.images[userId].image_url = fixImageUrl(
            data.images[userId].image_url
          );
        }
      });
    }

    return data;
  } catch (error) {
    console.warn("Batch user images request failed:", error);
    // Return empty result instead of throwing to prevent UI crashes
    return {
      images: {},
      total_requested: userIds?.length || 0,
      total_found: 0,
      error: error.message,
    };
  }
};

// Legacy patron-based methods (kept for backward compatibility)
const getPatronImageInfo = async (patronId) => {
  console.warn(
    "getPatronImageInfo is deprecated. Use getUserImageInfo instead."
  );
  // This would need to be implemented if you still need patron ID lookups
  throw new ProfileApiError(
    "Patron ID lookup is no longer supported. Use user ID instead.",
    "DEPRECATED"
  );
};

const getPatronImageByUserId = getUserImageInfo; // Redirect to user-based method

const getBatchPatronImages = async (patronIds) => {
  console.warn(
    "getBatchPatronImages is deprecated. Use getBatchUserImages instead."
  );
  throw new ProfileApiError(
    "Patron ID batch lookup is no longer supported. Use user IDs instead.",
    "DEPRECATED"
  );
};

const uploadImage = async (file, onProgress = null) => {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new ProfileApiError(validation.errors.join("; "), "VALIDATION_ERROR");
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percent);
      };
    }

    const response = await http.post(API_PATHS.uploadImage, formData, config);

    const data = response.data;
    if (!data.success) {
      throw new ProfileApiError(data.error || "Upload failed", "UPLOAD_ERROR");
    }

    data.image_url = fixImageUrl(data.image_url);
    return data;
  } catch (error) {
    handleError(error);
  }
};

const deleteImage = async () => {
  try {
    const response = await http.delete(API_PATHS.deleteImage);
    const data = response.data;

    if (!data.success) {
      throw new ProfileApiError(data.error || "Delete failed", "DELETE_ERROR");
    }

    return data;
  } catch (error) {
    handleError(error);
  }
};

const getProfileData = async () => {
  try {
    const response = await http.get(API_PATHS.profileData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

const updateProfile = async (profileData) => {
  try {
    const response = await http.put(API_PATHS.updateProfile, profileData);
    const data = response.data;

    if (!data.success) {
      throw new ProfileApiError(
        data.error || "Profile update failed",
        "UPDATE_ERROR"
      );
    }

    return data;
  } catch (error) {
    handleError(error);
  }
};

// Export validation constants for use in components
export const profileValidation = {
  MAX_FILE_SIZE: FILE_SIZE_LIMITS.MAX_5MB,
  ALLOWED_EXTENSIONS: ["png", "jpg", "jpeg", "gif", "webp"],
  ALLOWED_IMAGE_TYPES: EXTENDED_IMAGE_TYPES,
};

export const profileApi = {
  profileBaseURL: PROFILE_BASE_URL,
  getImageInfo,
  getUserImageInfo,
  getBatchUserImages,
  // Legacy methods (kept for backward compatibility but will throw warnings)
  getPatronImageInfo,
  getPatronImageByUserId,
  getBatchPatronImages,
  uploadImage,
  deleteImage,
  getProfileData,
  updateProfile,
  validateImageFile,
  ProfileApiError,
  FileValidator: {
    validateImageFile,
  },
  validation: profileValidation,
};

export {
  getImageInfo,
  getUserImageInfo,
  getBatchUserImages,
  // Legacy exports
  getPatronImageInfo,
  getPatronImageByUserId,
  getBatchPatronImages,
  uploadImage,
  deleteImage,
  getProfileData,
  updateProfile,
  validateImageFile,
  ProfileApiError,
};
